/** @typedef {import('@/types/api').BlockoutType} BlockoutType */

export const BLOCKOUT_PRESETS = [
  { value: 'lunch', label: 'Lunch break' },
  { value: 'training', label: 'Training' },
  { value: 'holiday', label: 'Holiday / closed' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'custom', label: 'Custom' },
]

/**
 * @param {BlockoutType} type
 */
export function blockoutTypeLabel(type) {
  switch (type) {
    case 'stylist':
      return 'Stylist'
    case 'chair':
      return 'Chair'
    case 'branch':
      return 'Branch'
    default:
      return type
  }
}

/**
 * @param {import('@/types/api').Blockout} blockout
 */
export function blockoutTargetLabel(blockout) {
  if (blockout.blockout_type === 'stylist') {
    return blockout.stylist?.name ?? 'Stylist'
  }

  if (blockout.blockout_type === 'chair') {
    return blockout.chair?.name ?? 'Chair'
  }

  return 'Whole branch'
}

/**
 * @param {string} iso
 */
export function formatBlockoutTime(iso) {
  const date = new Date(iso)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * @param {import('@/types/api').Blockout} blockout
 */
export function formatBlockoutRange(blockout) {
  if (blockout.all_day) {
    return 'All day'
  }

  return `${formatBlockoutTime(blockout.starts_at)} – ${formatBlockoutTime(blockout.ends_at)}`
}

/**
 * @param {{
 *   preset: string
 *   blockoutType: BlockoutType
 *   stylistId: string
 *   chairId: string
 *   title: string
 *   date: string
 *   startTime: string
 *   endTime: string
 *   allDay: boolean
 * }} form
 */
export function buildBlockoutPayload(form) {
  const preset = BLOCKOUT_PRESETS.find((item) => item.value === form.preset)
  const title =
    form.preset === 'custom' ? form.title.trim() : (preset?.label ?? form.title.trim())

  const startsAt = form.allDay
    ? `${form.date}T00:00:00`
    : `${form.date}T${form.startTime}:00`
  const endsAt = form.allDay ? `${form.date}T23:59:59` : `${form.date}T${form.endTime}:00`

  return {
    blockout_type: form.blockoutType,
    title,
    stylist_id: form.blockoutType === 'stylist' && form.stylistId ? Number(form.stylistId) : null,
    chair_id: form.blockoutType === 'chair' && form.chairId ? Number(form.chairId) : null,
    starts_at: startsAt,
    ends_at: endsAt,
    all_day: form.allDay,
  }
}

/**
 * @param {{
 *   blockoutType: BlockoutType
 *   stylistId: string
 *   chairId: string
 *   title: string
 *   date: string
 *   startTime: string
 *   endTime: string
 *   allDay: boolean
 * }} form
 * @returns {string[]}
 */
export function validateBlockoutForm(form) {
  const errors = []

  if (form.blockoutType === 'stylist' && !form.stylistId) {
    errors.push('Select a stylist.')
  }

  if (form.blockoutType === 'chair' && !form.chairId) {
    errors.push('Select a chair.')
  }

  if (form.preset === 'custom' && !form.title.trim()) {
    errors.push('Enter a blockout title.')
  }

  if (!form.date) {
    errors.push('Select a date.')
  }

  if (!form.allDay && (!form.startTime || !form.endTime)) {
    errors.push('Enter start and end times.')
  }

  if (!form.allDay && form.startTime >= form.endTime) {
    errors.push('End time must be after start time.')
  }

  return errors
}
